import { useMemo } from 'react';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { SelectInput } from '@interstate/components/SelectInput';
import { DatePicker } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { RevealableMaskTextInput } from '@makemydeal/dr-shared-components';
import {
    BUSINESS_TYPE_OPTIONS,
    FINANCIAL_STATEMENT_TYPE_OPTIONS,
    BUSINESS_TYPE_FIELD,
    BUSINESS_ESTABLISHED_DATE_FIELD,
    BUSINESS_NUMBER_OF_EMPLOYEES_FIELD,
    BUSINESS_STATE_OF_INCORPORATION_FIELD,
    BUSINESS_FINANCIAL_STATEMENT_DESCRIPTION_FIELD,
    BUSINESS_TAX_ID_NUMBER_FIELD,
    TAX_ID_MASK
} from '../../../constants';
import { FieldsRow } from '../sectionLayout.styles';
import { getFieldError } from './businessErrors.utils';

/**
 * Business Information field group (Tax ID, Business Type, Date Established, # Employees,
 * State of Incorporation, Financial Statement Type). Migrated as-is from MV.
 */
const BusinessInformationFields = () => {
    const { formValues, handleInputChange, handleNumberInputChange, isLocked, formErrors } = useCustomerCardContext();
    const businessErrors = formErrors?.business;
    const getError = (field: string) => getFieldError(businessErrors, field);
    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, StateCodes);

    const handleDateEstablishedChange = (event: InterstateOnChangeEvent<DatePickerEventValue>) => {
        const value = event.target.value;
        const dateValue = value.additionalFormats?.iso ? value.additionalFormats.iso.slice(0, 10) : '';
        handleInputChange(BUSINESS_ESTABLISHED_DATE_FIELD, dateValue);
    };

    return (
        <>
            <FieldsRow>
                <RevealableMaskTextInput
                    name={BUSINESS_TAX_ID_NUMBER_FIELD}
                    label="Tax ID Number"
                    data-testid="business-info-tax-id"
                    maskedValue={TAX_ID_MASK}
                    value={formValues.business?.taxIdNumber || ''}
                    onChange={handleInputChange}
                    disabled={isLocked}
                    hasError={getError('taxIdNumber').hasError}
                    errorMessage={getError('taxIdNumber').errorMessage}
                />
                <SelectInput
                    name="business-businessType"
                    displayLabel={true}
                    label="Business Type"
                    value={formValues.business?.businessType || ''}
                    onChange={(e) => handleInputChange(BUSINESS_TYPE_FIELD, e.target.value || '')}
                    options={BUSINESS_TYPE_OPTIONS}
                    data-testid="business-info-business-type"
                    size="small"
                    disabled={isLocked}
                />
                <DatePicker
                    name="business-dateEstablished"
                    label="Date Established"
                    value={
                        formValues.business?.businessEstablishedDate
                            ? new Date(formValues.business.businessEstablishedDate)
                            : undefined
                    }
                    onChange={handleDateEstablishedChange}
                    data-testid="business-info-date-established"
                    size="small"
                    disabled={isLocked}
                />
                <NumericInput
                    id="business-numberOfEmployees"
                    name="business-numberOfEmployees"
                    label="Number of Employees"
                    value={formValues.business?.numberOfEmployees?.toString() || ''}
                    onChange={(e) => handleNumberInputChange(BUSINESS_NUMBER_OF_EMPLOYEES_FIELD, e.target.value)}
                    data-testid="business-info-number-of-employees"
                    size="small"
                    disabled={isLocked}
                    allowDecimal={false}
                    hasError={getError('numberOfEmployees').hasError}
                    errorMessage={getError('numberOfEmployees').errorMessage}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                />
            </FieldsRow>
            <FieldsRow>
                <SelectInput
                    name="business-stateOfIncorporation"
                    label="State of Inc"
                    value={formValues.business?.stateOfIncorporation || ''}
                    onChange={(e) => handleInputChange(BUSINESS_STATE_OF_INCORPORATION_FIELD, e.target.value || '')}
                    options={stateSelectInputValues}
                    data-testid="business-info-state-of-inc"
                    size="small"
                    disabled={isLocked}
                />
                <SelectInput
                    name="business-financialStatementType"
                    displayLabel={true}
                    label="Fin. Statement Type (optional)"
                    value={formValues.business?.financialStatementDescription || ''}
                    onChange={(e) => handleInputChange(BUSINESS_FINANCIAL_STATEMENT_DESCRIPTION_FIELD, e.target.value || '')}
                    options={FINANCIAL_STATEMENT_TYPE_OPTIONS}
                    data-testid="business-info-financial-statement-type"
                    size="small"
                    disabled={isLocked}
                />
            </FieldsRow>
        </>
    );
};

BusinessInformationFields.displayName = 'BusinessInformationFields';
export default BusinessInformationFields;
